/**
 * Индивидуальный стиль объекта (docs/editing/plan.md §4.2).
 *
 * Хранится в служебных полях `properties` самого объекта, а НЕ в колонке
 * `features.style_overrides`. Причина: тайлы больших слоёв собираются из `properties`
 * (`jsonb_object_keys` в `sloy_public_vector_mvt`), поэтому такие поля доезжают до карты
 * сами — маленькие geojson-слои и большие тайловые ведут себя одинаково, отдельной
 * механики рендера не нужно.
 *
 * Значения всегда СТРОКИ. On-the-fly MVT отдаёт все свойства текстом
 * (`(f.properties->>k)::text` в 0010_mvt_point_sampling.sql), тогда как GeoJSON и PMTiles
 * сохраняют числа. Один контракт «в данных строка, в выражении приведение» избавляет от
 * ситуации «на маленьком слое работает, на большом молча ломается».
 *
 * Префикс `_sloyStyle` — зарезервированное пространство имён, а не просто `_`: импорт
 * кладёт пользовательские ключи как есть, и короткий `_styleColor` вполне может оказаться
 * настоящим полем пользователя.
 */

/** Цвет объекта: любой CSS-цвет, который понимает MapLibre (`to-color`). */
export const FEATURE_STYLE_COLOR_KEY = '_sloyStyleColor';

/** Размер: радиус точки в px / толщина линии в px. */
export const FEATURE_STYLE_SIZE_KEY = '_sloyStyleSize';

/** Непрозрачность 0..1. */
export const FEATURE_STYLE_OPACITY_KEY = '_sloyStyleOpacity';

/**
 * Вид линии объекта (docs/editing/plan.md §4.2.1). Значения — те же строки, что у полей
 * виз-блока линии (`LINE_DASH_STYLES`, `LINE_JOIN_STYLES`, `LINE_END_CAPS`,
 * `LINE_PATTERNS`); неизвестное значение выражение молча заменяет значением слоя.
 */
export const FEATURE_STYLE_LINE_DASH_KEY = '_sloyStyleLineDash';
export const FEATURE_STYLE_LINE_JOIN_KEY = '_sloyStyleLineJoin';
/** Окончание в НАЧАЛЕ линии (первая вершина). */
export const FEATURE_STYLE_LINE_START_KEY = '_sloyStyleLineStart';
/** Окончание в КОНЦЕ линии (последняя вершина). */
export const FEATURE_STYLE_LINE_END_KEY = '_sloyStyleLineEnd';
export const FEATURE_STYLE_LINE_PATTERN_KEY = '_sloyStyleLinePattern';

/** Ключи вида линии — отдельным списком: их сбрасывает одна кнопка блока «Линия объекта». */
export const FEATURE_LINE_STYLE_OVERRIDE_KEYS = [
  FEATURE_STYLE_LINE_DASH_KEY,
  FEATURE_STYLE_LINE_JOIN_KEY,
  FEATURE_STYLE_LINE_START_KEY,
  FEATURE_STYLE_LINE_END_KEY,
  FEATURE_STYLE_LINE_PATTERN_KEY,
] as const;

/** Все ключи индивидуального стиля — для тайлов, экспорта и скрытия из форм. */
export const FEATURE_STYLE_OVERRIDE_KEYS = [
  FEATURE_STYLE_COLOR_KEY,
  FEATURE_STYLE_SIZE_KEY,
  FEATURE_STYLE_OPACITY_KEY,
  ...FEATURE_LINE_STYLE_OVERRIDE_KEYS,
] as const;

export type FeatureStyleOverrideKey = (typeof FEATURE_STYLE_OVERRIDE_KEYS)[number];

/** Зарезервированное пространство имён: ключи с этим префиксом принадлежат системе. */
export const FEATURE_STYLE_KEY_PREFIX = '_sloyStyle';

export function isFeatureStyleOverrideKey(key: string): boolean {
  return key.startsWith(FEATURE_STYLE_KEY_PREFIX);
}
